/**
 * Web-specific AuthViewModel adapter
 * Integrates cross-platform AuthViewModel with web services and React Router
 */

import { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createAuthViewModel, createWebPlatformServices, type IPlatformServices, type INavigationService } from '@restaurant/viewmodels'
import { AuthService, isValidPhone, isValidEmail, useUIStore } from '@restaurant/core'

// Mirror useMobileAuthViewModel's createSheetDismissNavigationService: closing the
// auth sheet becomes the DIRECT action of navigateToHome(), so it no longer depends
// on a route change. Object.create preserves every other INavigationService method
// via the prototype chain (base is a WebNavigationService class instance, whose
// methods live on the prototype and would be lost by a plain spread).
function createSheetDismissNavigationService(
  base: INavigationService,
  closeSheet: () => void,
): INavigationService {
  const wrapped: INavigationService = Object.create(base)
  wrapped.navigateToHome = () => { closeSheet() }
  return wrapped
}
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'
import { useSessionViewModel } from '../adapters/WebSessionViewModel'
import { useValidationViewModel } from '../adapters/WebValidationViewModel'

// Use the singleton apiClient so debug mode sandbox routing is respected

/**
 * Web-specific AuthViewModel hook
 * Provides complete authentication flow management for web platform
 */
export const useAuthViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Get platform services. Make dismissing the auth sheet the DIRECT action of
  // navigateToHome() (see createSheetDismissNavigationService above) instead of
  // relying on useLayoutOrchestration's pathname-change effect — that effect
  // no-ops when sign-in completes while already on '/' (the menu, the usual
  // checkout entry), leaving the OTP sheet stuck open over an authenticated app.
  const basePlatformServices = createWebPlatformServices(navigate, location)
  const platformServices: IPlatformServices = {
    ...basePlatformServices,
    navigationService: createSheetDismissNavigationService(
      basePlatformServices.navigationService,
      () => useUIStore.getState().setIsAuthSheetOpen(false),
    ),
  }

  // Get dependent ViewModels
  const sessionViewModel = useSessionViewModel()
  const validationViewModel = useValidationViewModel()

  // Adapt ValidationViewModel to the synchronous-return interface AuthViewModel expects.
  // AuthViewModel calls validatePhone/validateEmail inside useMemo and expects a return
  // value { isValid, error }. The real ValidationViewModel methods return void and trigger
  // state updates, which would cause infinite re-renders. So we use pure computation only.
  const authValidationAdapter = useMemo(() => ({
    validatePhone(phone: string) {
      const valid = isValidPhone(phone || '')
      return { isValid: valid, error: valid ? null : 'Please enter a valid phone number' }
    },
    validateEmail(email: string) {
      const clean = (email || '').toLowerCase().trim()
      const valid = clean.length > 0 && isValidEmail(clean) && !clean.includes('..')
      return { isValid: valid, error: valid ? null : 'Please enter a valid email address' }
    },
    validateForm(data: Record<string, unknown>) {
      const errors: Record<string, string> = {}
      const fullName = (data.fullName as string || '').trim()
      if (!fullName) errors.fullName = 'Full name is required'
      // Email is optional — blank is valid. Validate shape only when provided,
      // mirroring FieldGroupAccountInformation (which treats blank as valid and
      // only blocks on a malformed address). Without this, a malformed email
      // reaches PUT /auth/profile and comes back as a raw Pydantic 400.
      const emailAddress = (data.emailAddress as string || '').trim().toLowerCase()
      if (emailAddress && (!isValidEmail(emailAddress) || emailAddress.includes('..'))) {
        errors.emailAddress = 'Please enter a valid email address'
      }
      return { isValid: Object.keys(errors).length === 0, errors }
    },
    clearErrors() {
      validationViewModel.resetFormValidation()
    },
    errors: {} as Record<string, string>,
  }), [validationViewModel])

  // Sync logger userId with auth state — set on login, clear on logout
  useEffect(() => {
    safeGetLogger()?.setUserId(sessionViewModel.isAuthenticated && sessionViewModel.user?.id
      ? sessionViewModel.user.id
      : null)
  }, [sessionViewModel.isAuthenticated, sessionViewModel.user?.id])

  // Create AuthService instance
  const authService = new AuthService({ apiClient })

  // Create and return the platform-agnostic AuthViewModel
  const AuthViewModelHook = createAuthViewModel(
    platformServices,
    authService,
    sessionViewModel,
    authValidationAdapter
  )

  return AuthViewModelHook()
}

// Re-export types for convenience
export type { 
  AuthViewModel, 
  AuthViewModelState, 
  AuthViewModelActions,
  AuthFlow,
  AccountInformation 
} from '@restaurant/viewmodels'